/**
 * Admin Accounts API client — /api/admin-accounts.php (every Estimator account).
 */

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

export interface AccountMembership {
  retailerId: number | null;
  retailerName: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'unmatched';
  role: 'viewer' | 'manager';
  allLocations: boolean;
  includeChildren?: boolean;
  locations: { id: number; name: string }[];
}

export interface AccountRow {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  authProvider: string;
  keycloakSub: string | null;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  estimateCount: number;
  commentCount: number;
  memberships: AccountMembership[];
  /** Admin-typed partner_location_members rows. */
  manualLocationCount: number;
}

export interface AccountsResponse {
  accounts: AccountRow[];
  pagination: { page: number; per_page: number; total: number; total_pages: number };
  /** True when the estimator-backend service account is configured, so Keycloak users can be deleted too. */
  keycloakAdmin: boolean;
}

export interface DeleteAccountResult {
  deleted: boolean;
  id: number;
  email: string;
  unlinked: Record<string, number>;
  keycloak: 'deleted' | 'skipped' | 'not_configured' | 'no_keycloak_user' | 'failed';
  keycloakError: string | null;
}

async function handle<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { error?: string }).error || `Request failed (${res.status})`);
  return body as T;
}

export async function fetchAccounts(q: { search?: string; page?: number; perPage?: number }): Promise<AccountsResponse> {
  const p = new URLSearchParams();
  if (q.search) p.set('search', q.search);
  if (q.page) p.set('page', String(q.page));
  if (q.perPage) p.set('per_page', String(q.perPage));
  const res = await fetch(`${API_BASE}/admin-accounts.php?${p}`, { credentials: 'include' });
  return handle<AccountsResponse>(res);
}

export async function deleteAccount(id: number, alsoKeycloak: boolean): Promise<DeleteAccountResult> {
  const res = await fetch(`${API_BASE}/admin-accounts.php?id=${id}${alsoKeycloak ? '&keycloak=1' : ''}`, { method: 'DELETE', credentials: 'include' });
  return handle<DeleteAccountResult>(res);
}

// ---------------------------------------------------------------------------
// Access (what one account holds, and setting it by hand)
// ---------------------------------------------------------------------------

export interface AccessLocation { id: number; name: string; city: string | null; retailerId: number }
export interface AccessRetailerOption { id: number; name: string; parentRetailerId: number | null; isActive: boolean }
export interface AccessMembership {
  id: number;
  retailerId: number;
  retailerName: string;
  status: 'pending' | 'approved' | 'rejected' | 'unmatched';
  role: 'viewer' | 'manager';
  /** The whole node: every location, incl. ones added later. */
  allLocations: boolean;
  /** With allLocations on a top-level retailer: its sub-retailers too. */
  includeChildren: boolean;
  locations: { id: number; name: string; city: string | null }[];
  source: string;
}

/** One tick in the access editor. */
export type AccessGrant =
  | { type: 'retailer'; id: number; includeChildren?: boolean; role?: 'viewer' | 'manager' }
  | { type: 'location'; id: number };
export interface AccountAccess {
  account: { id: number; email: string; firstName: string | null; lastName: string | null; jobTitle?: string };
  memberships: AccessMembership[];
  /** Open proposals with no retailer yet (shown so the admin knows the matcher could not decide). */
  unplaced: { id: number; status: string; source: string; sfAccountName: string | null; matchedDomain: string | null }[];
  retailers: AccessRetailerOption[];
  locations: AccessLocation[];
}

export async function fetchAccountAccess(userId: number): Promise<AccountAccess> {
  const res = await fetch(`${API_BASE}/admin-accounts.php?id=${userId}`, { credentials: 'include' });
  return handle<AccountAccess>(res);
}

export async function setAccountAccess(body: { userId: number; retailerId: number; role: 'viewer' | 'manager'; allLocations: boolean; locationIds: number[] }): Promise<AccountAccess> {
  const res = await fetch(`${API_BASE}/admin-accounts.php`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'set_access', ...body }) });
  return handle<AccountAccess>(res);
}

/** Rewrite the account's access in retailerId's whole tree from grants (empty = remove from the tree). */
export async function setAccountGrants(body: { userId: number; retailerId: number; role: 'viewer' | 'manager'; grants: AccessGrant[] }): Promise<AccountAccess> {
  const res = await fetch(`${API_BASE}/admin-accounts.php`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'set_access', ...body }) });
  return handle<AccountAccess>(res);
}

export async function setAccountProfile(userId: number, jobTitle: string): Promise<AccountAccess> {
  const res = await fetch(`${API_BASE}/admin-accounts.php`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'set_profile', userId, jobTitle }) });
  return handle<AccountAccess>(res);
}

export async function removeAccountAccess(userId: number, retailerId: number): Promise<AccountAccess> {
  const res = await fetch(`${API_BASE}/admin-accounts.php`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'remove_access', userId, retailerId }) });
  return handle<AccountAccess>(res);
}
